import React, { useState } from 'react';
import Swal from 'sweetalert2';
import { Navigate } from 'react-router-dom';
import Navbar from '../../../../ReusableBlocks/Navbar';
import MasterDataFieldPanel from '../Components/panels/MasterDataFieldPanel';
import PageHeader from '../Components/shared/PageHeader';
import { PROJECT_MASTER_DATA_FIELDS } from '../Constants/projectMasterDataFields';
import { PARTNER_PORTAL_NOT_AUTHORIZED } from '../Constants/partnerRoutes';
import { useProjectMasterData } from '../Hooks/useProjectMasterData';
import { usePartnerRoleAccess } from '../Hooks/usePartnerRoleAccess';
import PartnerLayout from '../Layout/PartnerLayout';
import {
  getPartnerApiErrorMessage,
  showPartnerActionErrorPopup,
} from '../Utils/partnerApiFeedback';

const PAEOperationsPage = () => {
  const access = usePartnerRoleAccess();
  const {
    masterData,
    loading,
    error,
    saving,
    addItem,
    updateItem,
    deleteItem,
  } = useProjectMasterData(access.canPerformPAEOperations);
  const [activeFieldKey, setActiveFieldKey] = useState(PROJECT_MASTER_DATA_FIELDS[0].key);
  const [actionMessage, setActionMessage] = useState('');

  const activeField = PROJECT_MASTER_DATA_FIELDS.find((field) => field.key === activeFieldKey)
    || PROJECT_MASTER_DATA_FIELDS[0];

  const handleAddItem = async (field, value) => {
    setActionMessage('');

    try {
      const response = await addItem(field, value);
      setActionMessage(response?.message || 'Dropdown value added successfully.');
    } catch (saveError) {
      const message = getPartnerApiErrorMessage(saveError, 'Failed to add dropdown value.');
      await showPartnerActionErrorPopup(message, { isConflict: saveError?.status === 409 });
      throw saveError;
    }
  };

  const handleUpdateItem = async (field, oldValue, newValue) => {
    setActionMessage('');

    try {
      const response = await updateItem(field, oldValue, newValue);
      setActionMessage(response?.message || 'Dropdown value updated successfully.');
    } catch (saveError) {
      const message = getPartnerApiErrorMessage(saveError, 'Failed to update dropdown value.');
      await showPartnerActionErrorPopup(message, { isConflict: saveError?.status === 409 });
      throw saveError;
    }
  };

  const handleDeleteItem = async (field, value) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Delete dropdown value?',
      text: `Remove "${value}" from ${activeField.label}?`,
      showCancelButton: true,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#b42318',
      cancelButtonColor: '#64748b',
      customClass: {
        title: 'swal-title-custom',
        htmlContainer: 'swal-text-custom',
      },
    });

    if (!result.isConfirmed) {
      return;
    }

    setActionMessage('');

    try {
      const response = await deleteItem(field, value);
      setActionMessage(response?.message || 'Dropdown value deleted successfully.');
    } catch (saveError) {
      const message = getPartnerApiErrorMessage(saveError, 'Failed to delete dropdown value.');
      await showPartnerActionErrorPopup(message, { isConflict: saveError?.status === 409 });
    }
  };

  if (!access.canPerformPAEOperations) {
    return <Navigate to={PARTNER_PORTAL_NOT_AUTHORIZED} replace />;
  }

  return (
    <>
      <Navbar activeTab="PARTNER_PORTAL" />
      <PartnerLayout access={access}>
        <PageHeader
          title="PAE Operations"
          description="Manage dropdown values used in partner project forms — products, engagement types, deployment models, competitive context, and project status."
          rightContent={<span className="psm-queue-view-pill">PAE View</span>}
        />

        <div className="partner-pae-operations-page">
        <div className="pae-master-data-info-banner">
          PAE-only configuration. Changes here update the dropdown lists used across the Partner Portal.
        </div>

        {actionMessage ? <p className="partner-form-complete-indicator">{actionMessage}</p> : null}
        {error ? <p className="text-danger">{error}</p> : null}

        {loading ? (
          <p className="partner-loading-text">Loading dropdown configuration...</p>
        ) : (
          <div className="pae-master-data-layout">
            <aside className="pae-master-data-tabs">
              {PROJECT_MASTER_DATA_FIELDS.map((field) => {
                const count = (masterData[field.key] || []).length;
                const isActive = field.key === activeFieldKey;

                return (
                  <button
                    key={field.key}
                    type="button"
                    className={`pae-master-data-tab ${isActive ? 'is-active' : ''}`}
                    onClick={() => setActiveFieldKey(field.key)}
                  >
                    <span>{field.label}</span>
                    <span className="pae-master-data-tab-count">{count}</span>
                  </button>
                );
              })}
            </aside>

            <MasterDataFieldPanel
              field={activeField}
              items={masterData[activeField.key] || []}
              saving={saving}
              onAddItem={handleAddItem}
              onUpdateItem={handleUpdateItem}
              onDeleteItem={handleDeleteItem}
            />
          </div>
        )}
        </div>
      </PartnerLayout>
    </>
  );
};

export default PAEOperationsPage;
